import { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useStores } from "../../../stores/setup/use-store";
import { ILocationFormValues, CoordinatesType, ILocation } from "../types/";
import { useLocationGeospatial } from "./useLocationGeospatial";
import * as Yup from "yup";
import { StoreState } from "enum/StoreState";
import { IStation } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";

export const useLocationDrawer = () => {
  const { t } = useTranslation("translation", { keyPrefix: "locations" });
  const {
    dataStores: {
      locationStore,
      manageStationStore,
      forecastChartPageStore,
      insightAlertStore,
    },
  } = useStores();
  const geospatial = useLocationGeospatial();
  const [isDropZoneVisible, setIsDropZoneVisible] = useState(false);
  const [initialValues, setInitialValues] = useState<ILocationFormValues>({
    name: "",
    coordinatesType: CoordinatesType.AREA,
    coordinates: [],
  });

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required(t("requiredField") as string),
        coordinatesType: Yup.string().required(t("requiredField") as string),
        coordinates: Yup.array().min(1, t("coordinatesRequired") as string),
      }),
    [t]
  );

  const handleOpenDrawer = useCallback(
    async (location?: ILocation) => {
      locationStore.setDrawerOpen(true);

      const createLocationFormData = (
        location: Partial<ILocation>
      ): ILocationFormValues => ({
        name: location.name || "",
        coordinatesType: location.coordinatesType || CoordinatesType.AREA,
        coordinates: location.coordinates || [],
      });

      const getDefaultFormValues = (): ILocationFormValues => ({
        name: "",
        coordinatesType: CoordinatesType.AREA,
        coordinates: [],
      });

      const handleEditingFallback = (location: ILocation): void => {
        locationStore.setSelectedLocation(location);
        locationStore.setSelectedCoordinates(location.coordinates || []);

        const formData = createLocationFormData(location);
        setInitialValues(formData);
        locationStore.setCreateFormValues(formData);
      };

      const setupEditingMode = async (location: ILocation): Promise<void> => {
        locationStore.setIsEditing(true);

        try {
          const fullLocation = await locationStore.getLocationByIdAPI(
            location.id
          );
          const formData = createLocationFormData(fullLocation);

          setInitialValues(formData);
          locationStore.setCreateFormValues(formData);
        } catch (error) {
          console.error("Error loading location:", error);
          handleEditingFallback(location);
        }
      };

      const setupCreateMode = (): void => {
        locationStore.setSelectedLocation(null);
        locationStore.setIsEditing(false);
        locationStore.setSelectedCoordinates([]);

        const formData = locationStore.createFormValues
          ? {
            ...locationStore.createFormValues,
            coordinates:
              locationStore.selectedCoordinates.length > 0
                ? locationStore.selectedCoordinates
                : locationStore.createFormValues.coordinates,
          }
          : getDefaultFormValues();

        setInitialValues(formData);
      };

      const isEditingExistingLocation = (
        location?: ILocation
      ): location is ILocation => Boolean(location?.id);

      if (isEditingExistingLocation(location)) {
        await setupEditingMode(location);
      } else {
        setupCreateMode();
      }
    },
    [locationStore, setInitialValues]
  );

  const handleOpenModal = useCallback(
    async (stationId: number, regionId: number) => {
      locationStore.setIsStationModalOpen(true);
      try {
        const fullStation = await manageStationStore.getStationById(
          stationId,
          regionId
        );

        if (fullStation) {
          locationStore.setEditStation(fullStation);
        }
      } catch (error) {
        console.error("Error loading station:", error);
      }
    },
    [locationStore, manageStationStore]
  );

  useEffect(() => {
    const hasCoordinates = locationStore.selectedCoordinates.length > 0;
    const isDrawerOpen = locationStore.isDrawerOpen;
    const isEditing = locationStore.isEditing;
    const hasFormValues = Boolean(locationStore.createFormValues);

    if (!isDrawerOpen) return;

    const getResolvedCoordinates = (): ILocationFormValues["coordinates"] => {
      if (hasCoordinates) {
        return locationStore.selectedCoordinates;
      }
      if (hasFormValues && locationStore.createFormValues?.coordinates) {
        return locationStore.createFormValues.coordinates;
      }
      return [];
    };

    const updateCreateModeValues = (): void => {
      if (hasFormValues && locationStore.createFormValues) {
        setInitialValues({
          ...locationStore.createFormValues,
          coordinates: getResolvedCoordinates(),
        });
      }
    };

    const updateCoordinatesOnly = (): void => {
      if (hasCoordinates) {
        setInitialValues((prev) => {
          const baseValues =
            hasFormValues && locationStore.createFormValues
              ? locationStore.createFormValues
              : prev;

          return {
            ...baseValues,
            coordinates: locationStore.selectedCoordinates,
          };
        });
      }
    };

    if (isEditing) {
      updateCoordinatesOnly();
    } else if (hasFormValues) {
      updateCreateModeValues();
    }
  }, [
    locationStore.isDrawerOpen,
    locationStore.isEditing,
    locationStore.selectedCoordinates,
    locationStore.createFormValues,
  ]);

  const handleCloseDrawer = useCallback(() => {
    locationStore.setDrawerOpen(false);
    locationStore.setSelectedLocation(null);
    locationStore.setIsEditing(false);
    locationStore.setCreateFormValues(null);
    locationStore.setSelectedCoordinates([]);
    insightAlertStore.setIsGeofenceInvalid(false);
    insightAlertStore.setIsGeofenceSavedToLocation(false);

    setInitialValues({
      name: "",
      coordinatesType: CoordinatesType.AREA,
      coordinates: [],
    });
  }, [locationStore, insightAlertStore]);

  const handleCloseModal = useCallback(() => {
    locationStore.setIsStationModalOpen(false);
    locationStore.setEditStation(null);
  }, [locationStore]);

  const handleSubmit = useCallback(
    async (values: ILocationFormValues) => {
      try {
        if (locationStore.isEditing && locationStore.selectedLocation) {
          await locationStore.updateLocationAPI(
            locationStore.selectedLocation.id,
            values
          );
        } else {
          await locationStore.createLocationAPI(values);
        }
        handleCloseDrawer();
      } catch (error) {
        console.error("Erro ao salvar localidade:", error);
      }
    },
    [locationStore, handleCloseDrawer]
  );

  const handleSaveEditStation = useCallback(
    async (station: IStation) => {
      const response = await manageStationStore.updateStation(station);

      if (response?.success) {
        forecastChartPageStore.updateStationDataHistory(
          station.station_id,
          station.region_id!,
          station.data_history || false
        );

        handleCloseModal();
      }
    },
    [manageStationStore, forecastChartPageStore, handleCloseModal]
  );

  return {
    initialValues,
    validationSchema,
    handleOpenDrawer,
    handleCloseDrawer,
    handleOpenModal,
    handleCloseModal,
    handleSubmit,
    handleSaveEditStation,
    editStation: locationStore.editStation,
    isOpen: locationStore.isDrawerOpen,
    isModalOpen: locationStore.isStationModalOpen,
    isEditing: locationStore.isEditing,
    isLoading: locationStore.drawerState === StoreState.PEDDING,
    isStationLoading: manageStationStore.state === StoreState.PEDDING,
    isDropZoneVisible,
    setIsDropZoneVisible,
    ...geospatial,
  };
};
